import { MutableRefObject, useEffect, useState } from 'react'
import ReactFC from 'react-fusioncharts'
import { CategoryToolTipProps } from './CategoryToolTip'
import styles from './MSLine.module.scss'
import { ChartType, Series } from './MSLine.types'
import { ToolTipContentProps } from './ToolTipContent'

export const dataSource = {
  chart: {
    baseFont: 'Open Sans',
    baseFontSize: '12',
    baseFontColor: styles.primaryContent,
    yAxisName: 'Hours',
    yAxisNameFontColor: styles.placeholderContent,
    yAxisNameFontBold: '0',
    numberPrefix: undefined,
    xAxisName: 'Period',
    xAxisNameFontColor: styles.placeholderContent,
    xAxisNameFontBold: '0',
    theme: 'fusion',
    legendPosition: 'top-left',
    showBorder: '0',
    showPlotBorder: '0',
    showLegendBorder: '0',
    bgAlpha: 1,
    // bgColor: 'transparent',
    showValues: '0',
    showAlternateHGridColor: '0',
    divLineAlpha: '10',
    legendBorderThickness: '0',
    canvasBorderThickness: '0',
    // canvasBgColor: 'transparent',
    canvasBgAlpha: 0.1,
    legendShadow: '0',
    showLegend: 0,
    numDivLines: 8,
    anchorRadius: 5,
    anchorBgAlpha: 100,
    paletteColors: '#3AABF2, #254291, #70D6C6, #2985AE, #B021D9, #2D65FF',
    toolTipBorderRadius: '2',
    toolTipBorderThickness: '0',
    toolTipBgColor: '#11174B',
    toolTipBgAlpha: '90',
    toolTipGrayOutColor: styles.secondaryContent,
    toolTipColor: styles.white,
    // toolTipFontFamily: 'Open Sans',
    // toolTipFontSize: '14',
    // toolTipFontWeight: 'bold',
    showToolTipShadow: '0',
    plotHoverEffect: '1',
    usePlotGradientColor: '0',
    // plotFillHoverColor: '#00ffaa',
    // plotBorderHoverThickness: '1',
    // plotBorderHoverDashed: '1',
    // plotBorderHoverDashLen: '6',
    // plotBorderHoverDashGap: '2',
    // drawCrossLine: '1',
    plotToolText: `
    <div id='divToolTip' class=${styles.toolTipContainer} />
    `
  },
  categories: [
    {
      category: [
        {
          label: 'Mon'
        },
        {
          label: 'Tue'
        },
        {
          label: 'Wed'
        },
        {
          vline: 'true',
          lineposition: '0',
          color: '#6baa01',
          labelHAlign: 'center',
          labelPosition: '0',
          label: 'National holiday',
          dashed: '1'
        },
        {
          label: 'Thu'
        },
        {
          label: 'Fri'
        },
        {
          label: 'Sat'
        },
        {
          label: 'Sun'
        }
      ]
    }
  ],
  dataset: [
    {
      seriesname: 'Bakersfield Central',
      anchorBgColor: '#254291',
      color: '#254291',
      data: [
        {
          value: '15123'
        },
        {
          value: '14233'
        },
        {
          value: '25507'
        },
        {
          value: '9110'
        },
        {
          value: '15529'
        },
        {
          value: '20803'
        },
        {
          value: '19202'
        }
      ]
    }, {
      seriesname: 'Los Angeles Topanga',
      data: [
        {
          value: '13400'
        },
        {
          value: '12800'
        },
        {
          value: '22800'
        },
        {
          value: '12400'
        },
        {
          value: '15800'
        },
        {
          value: '19800'
        },
        {
          value: '21800'
        }
      ]
    }
  ]
}

export const useCategoryToolTipProps = (
  chartContainer: MutableRefObject<ReactFC>,
  dataSource: any,
  chartType: ChartType
): { categoryToolTipProps: CategoryToolTipProps } => {
  const [categoryToolTipProps, setCategoryToolTipProps] = useState<CategoryToolTipProps>(null)

  useEffect(() => {
    const applyEffect = () => {
      if (!chartContainer.current) return

      const container = (chartContainer.current as any).chartObj.container
      const textTagsAll: HTMLElement[] = Array.from(container.querySelectorAll('text'))

      const categoryLabels: string[] = dataSource?.categories?.[0]?.category?.map(category => category.label) || []

      // the offsetY of the highest positioned text tag HTML element
      let lowestYAttribute = null

      const categoryLabelTextTags: HTMLElement[] = textTagsAll
        .filter((textTag) => {
          // using the For-loop to find the lowest Y attribute
          const attrY = Math.round(textTag.getAttribute('y') as any)
          lowestYAttribute = lowestYAttribute
            ? Math.min(lowestYAttribute, attrY)
            : attrY
          // end
          return categoryLabels.includes(textTag.textContent)
        })

      // the difference between the Y of the highest positioned text tag and
      // the Y of a category label text tag should be close enough to the actual
      // height of the graphics canvas container
      const graphContainerHeight = Math.round(categoryLabelTextTags[0]?.getAttribute('y') as any) - lowestYAttribute

      categoryLabelTextTags.forEach(textTag => {
        // onMouseEnter
        textTag.addEventListener('mouseenter', (ev: MouseEvent) => {
          textTag.style.fontWeight = 'bold'
          if (textTag.children && textTag.children.length) {
            Array.from(textTag.children).forEach((child: HTMLElement) => {
              child.style.fontWeight = 'bold'
            })
          }

          const toolTipContentProps: ToolTipContentProps = {
            header: { label: dataSource.chart.yAxisName, value: textTag.textContent },
            list: dataSource.dataset
              .filter((series: Series) => !series.isBest)
              .map((series: Series) => {
                return {
                  label: series.seriesname,
                  color: series.anchorBgColor,
                  image: series.image,
                  value: `
                ${dataSource.chart.numberPrefix || ''}${series.data?.[categoryLabels.findIndex(label => label === textTag.textContent)]?.value}
                `
                }
              })
          }
          const left = Math.round((ev as any).target.getAttribute('x')) + (ev.clientX - ev.offsetX)
          const top = Math.round((ev as any).target.getAttribute('y')) + (ev.clientY - ev.offsetY) - graphContainerHeight - 10

          setCategoryToolTipProps({
            style: {
              height: graphContainerHeight,
              top,
              left
            },
            toolTipContentProps
          })
        }, false)

        // onMouseLeave
        textTag.addEventListener('mouseleave', (_: MouseEvent) => {
          textTag.style.fontWeight = 'normal'

          if (textTag.childElementCount) {
            Array.from(textTag.children).forEach((child: HTMLElement) => {
              child.style.fontWeight = 'normal'
            })
          }

          setCategoryToolTipProps(null)
        }, false)
      })
    }

    // set timeout to delay the effect application
    // to ensure all graphics canvas and html elements are rendered/mounted
    setTimeout(applyEffect, 500)
  }, [chartContainer, dataSource, dataSource.dataset, chartType])

  return { categoryToolTipProps }
}
